import React, { useState, ReactNode, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '~/UIKit';
import { CHARCOAL_GREY, GREY } from '~/Constants/Colors.constants';
import { ILocation } from '~/Models/DTO/Location.models';
import { LocationSelectionModal } from '../LocationSelectionModal';
import { selectLocationById } from '~/store/provider';
import { ChevronRight } from '@mui/icons-material';
import { makeStyles } from '~/UIKit/Style';

interface LocationSelectionInputProps {
  selectedLocationId: string;
  children: ReactNode;
  updateLocation: (location: ILocation) => void;
  disabled?: boolean;
}

export const useLocationSelectionInput = ({
  updateLocation,
  selectedLocationId,
}: {
  updateLocation: (location: ILocation) => void;
  selectedLocationId: string;
}) => {
  // State
  const [open, setOpen] = useState(false);

  const handleLocationUpdate = (location: ILocation) => {
    updateLocation(location);
  };

  const currentLocation = useSelector(selectLocationById(selectedLocationId));
  const locationName = currentLocation?.name;

  return {
    open,
    setOpen,
    handleLocationUpdate,
    locationName,
  };
};

const useStyles = (locationSelected: boolean) =>
  makeStyles(() => ({
    locationSelectionInputWrapper: {
      width: '100%',
      display: 'flex',
    },
    button: {
      color: locationSelected ? CHARCOAL_GREY : GREY,
      fontWeight: 'normal',
      marginLeft: 'auto',
      paddingRight: 0,
      // Offsets Chevron padding
      marginRight: '-8px',
    },
    ChevronRight: {
      color: GREY,
      display: 'inherit',
      alignItems: 'inherit',
      justifyContent: 'inherit',
    },
  }));

export const LocationSelectionInput = ({
  children,
  selectedLocationId,
  updateLocation,
  disabled,
}: LocationSelectionInputProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { classes } = useStyles(!!selectedLocationId)();
  const { open, setOpen, handleLocationUpdate, locationName } = useLocationSelectionInput({
    updateLocation,
    selectedLocationId,
  });
  return (
    <div className={classes.locationSelectionInputWrapper}>
      <Button
        ref={buttonRef}
        type="button"
        className={classes.button}
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        {locationName ? locationName : children}
        <span className={classes.ChevronRight}>
          <ChevronRight />
        </span>
      </Button>
      <LocationSelectionModal
        open={open}
        onClose={() => setOpen(false)}
        updateLocation={handleLocationUpdate}
        selectedLocationId={selectedLocationId}
        anchorEl={buttonRef.current}
      />
    </div>
  );
};
